import * as ToggleGroup from '@radix-ui/react-toggle-group'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { ComponentUtils } from '@Components/utils'

import PreviousChevron from '@Assets/icons/previous-chevron-small.svg'
import NextChevron from '@Assets/icons/next-chevron.svg'

import './week-date-picker.scss'

const CLASS_NAME = 'week-date-picker'

type Props = {
  initialDateISOString?: string
  onDateChange: (date: DateTime) => void
}

export const WeekDatePicker = ({ initialDateISOString, onDateChange }: Props) => {
  const [selected, setSelected] = useState<DateTime>(
    initialDateISOString ? DateTime.fromISO(initialDateISOString) : DateTime.local(),
  )

  const startOfWeek = selected.startOf('week')
  const weekDays = Array.from({ length: 5 }).map((_, i) => startOfWeek.plus({ days: i }))

  const handlePrev = () => {
    let newDate = selected.minus({ day: 1 })
    while (!ComponentUtils.isWeekday(newDate)) {
      newDate = newDate.minus({ days: 1 })
    }
    setSelected(newDate)
    onDateChange(newDate)
  }

  const handleNext = () => {
    let newDate = selected.plus({ day: 1 })
    while (!ComponentUtils.isWeekday(newDate)) {
      newDate = newDate.plus({ days: 1 })
    }
    setSelected(newDate)
    onDateChange(newDate)
  }

  const handleSelect = (iso: string) => {
    if (iso === '') {
      return
    }
    const newDate = DateTime.fromISO(iso)
    setSelected(newDate)
    onDateChange(newDate)
  }

  return (
    <div className={CLASS_NAME}>
      <h3>{selected.toFormat('LLL yyyy')}</h3>

      <div className={`${CLASS_NAME}__row`}>
        <button
          className={`${CLASS_NAME}__nav-btn`}
          onClick={handlePrev}
          aria-label="Previous week">
          <img src={PreviousChevron} alt="" aria-hidden="true" />
        </button>

        <ToggleGroup.Root
          type="single"
          value={selected.toISODate() ?? ''}
          onValueChange={handleSelect}
          className={`${CLASS_NAME}__toggle-group`}
          aria-label="Select a date"
          role="radiogroup">
          {weekDays.map((day) => (
            <ToggleGroup.Item
              key={day.toISODate()}
              value={day.toISODate() ?? ''}
              className={`${CLASS_NAME}-toggle-day`}
              role="radio"
              aria-checked={selected.toISODate() === day.toISODate()}>
              <div className={`${CLASS_NAME}-toggle-day__label`}>{day.toFormat('ccc')}</div>
              <div className={`${CLASS_NAME}-toggle-day__number`}>{day.day}</div>
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>

        <button className={`${CLASS_NAME}__nav-btn`} onClick={handleNext} aria-label="Next week">
          <img src={NextChevron} alt="" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
